/**
 * S3 presigned PUT helpers.
 * Always use fetch — never the shared axios instance (API interceptor injects
 * currentUserId and can replace a File body with ~44B JSON).
 */

const EXT_CONTENT_TYPES = {
  '.pdf': 'application/pdf',
  '.doc': 'application/msword',
  '.docx':
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  '.xls': 'application/vnd.ms-excel',
  '.xlsx':
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  '.ppt': 'application/vnd.ms-powerpoint',
  '.pptx':
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  '.csv': 'text/csv',
  '.txt': 'text/plain',
  '.png': 'image/png',
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.gif': 'image/gif',
  '.webp': 'image/webp',
  '.bmp': 'image/bmp',
  '.zip': 'application/zip',
  '.rar': 'application/vnd.rar',
}

function getExtension(fileName) {
  const name = String(fileName || '')
  const dot = name.lastIndexOf('.')
  if (dot < 0) return ''
  return name.slice(dot).toLowerCase()
}

/**
 * Resolve Content-Type for upload. Browsers often leave file.type empty for
 * docx/xlsx — fall back by extension so presign + PUT stay aligned.
 */
export function resolveFileContentType(file) {
  const fromBrowser = String(file?.type || '').trim()
  if (fromBrowser) return fromBrowser

  const ext = getExtension(file?.name)
  if (ext && EXT_CONTENT_TYPES[ext]) return EXT_CONTENT_TYPES[ext]

  return 'application/octet-stream'
}

/**
 * PUT a File/Blob to a presigned S3 URL via raw fetch (no axios).
 *
 * contentType MUST be files[i].contentType from the pre-sign response
 * (same value the URL was signed with).
 */
export async function putFileToPresignedUrl(uploadUrl, file, contentType) {
  if (!uploadUrl || typeof uploadUrl !== 'string') {
    throw new Error('Missing S3 upload URL')
  }
  if (!(file instanceof Blob)) {
    throw new Error(`Upload body must be a File/Blob, got ${typeof file}`)
  }
  if (file.size <= 0) {
    throw new Error(`Refusing to upload empty file: ${file.name || 'unknown'}`)
  }

  const type = String(contentType || '').trim()
  if (!type) {
    throw new Error(
      `Missing Content-Type for S3 upload (${file.name || 'file'}). Use files[i].contentType from the pre-sign response.`
    )
  }

  // Defense: never allow a plain object (e.g. { currentUserId }) as the body.
  if (Object.prototype.toString.call(file) === '[object Object]') {
    throw new Error('Upload body is a plain object, not a File/Blob')
  }

  const response = await fetch(uploadUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': type,
    },
    body: file, // raw File/Blob — NOT FormData, NOT JSON
  })

  if (!response.ok) {
    const detail = await response.text().catch(() => '')
    throw new Error(
      `S3 upload failed for ${file.name || 'file'} (HTTP ${response.status})${
        detail ? `: ${detail.slice(0, 200)}` : ''
      }`
    )
  }

  return response
}

